'use es6';

import { combineReducers } from 'redux';
import Notifications from 'ReduxMessenger/reducers/Notifications';

// Reducers from CEUI   ---------------------------------------------------------------------

// App
import appStatusReducer from 'ContentEditorUI/redux/reducers/appStatusReducer';
import authReducer from 'ContentEditorUI/redux/reducers/authReducer';
import extraInitialStateReducer from 'ContentEditorUI/redux/reducers/extraInitialStateReducer';
import contentSchemaReducer from 'ContentEditorUI/redux/reducers/contentSchemaReducer';
import panesVisitStatusReducer from 'ContentEditorUI/redux/reducers/panesVisitStatusReducer';
import portalReducer from 'ContentEditorUI/redux/reducers/portalReducer';
import usersReducer from 'ContentEditorUI/redux/reducers/usersReducer';
import userAttributesReducer from 'ContentEditorUI/redux/reducers/userAttributesReducer';
import locallyCachedSettingsReducer from 'ContentEditorUI/redux/reducers/locallyCachedSettingsReducer';
import selectionReducer from 'ContentEditorUI/redux/reducers/selectionReducer';
import iframesReducer from 'ContentEditorUI/redux/reducers/iframesReducer';
import brandSettingsReducer from 'ContentEditorUI/redux/reducers/brandSettingsReducer';
import baseSettingsReducer from 'ContentEditorUI/redux/reducers/settings/baseSettingsReducer';
import modalReducer from 'ContentEditorUI/redux/reducers/modalReducer';
import createCollaborationSidebarReducer from 'ContentEditorUI/redux/reducers/createCollaborationSidebarReducer';
import locationReducer from 'ContentEditorUI/redux/reducers/locationReducer';
import cmsSettingsReducer from 'ContentEditorUI/redux/reducers/cmsSettingsReducer';

// Modules
import justSavedModuleReducer from 'ContentEditorUI/redux/reducers/justSavedModuleReducer';
import editSidebarReducer from 'ContentEditorUI/redux/reducers/editSidebarReducer';
import moduleSchemaReducer from 'ContentEditorUI/redux/reducers/moduleSchemaReducer';
import moduleReducer, { compareModuleReducerSlices, moduleReducerAsJSObject } from 'ContentEditorUI/redux/reducers/moduleReducer';
import { enableModuleReducerDiffing } from 'ContentEditorUI/utils/moduleReducerUtils';
import createUndoableModuleReducer, { createUndoableModuleReducerAsPOJO } from 'ContentEditorUI/redux/reducers/undoableModuleReducer';
import moduleErrorReducer from 'ContentEditorUI/redux/reducers/moduleErrorReducer';
import smartViewReducer from 'ContentEditorUI/redux/reducers/smartViewReducer';
import deleteRequestReducer from 'ContentEditorUI/redux/reducers/deleteRequestReducer';
import moduleVersionsReducer from 'ContentEditorUI/redux/reducers/moduleVersionsReducer';

// Content
import resources from 'ContentData/reducers/Resources';
import editingInfoReducer from 'ContentEditorUI/redux/reducers/editingInfoReducer';
import saveDataReducer from 'ContentEditorUI/redux/reducers/saveDataReducer';
import formStatusReducer from 'ContentEditorUI/redux/reducers/formStatusReducer';
import revisionsReducer from 'ContentEditorUI/redux/reducers/revisionsReducer';
import siteSettingsReducer from 'ContentEditorUI/redux/reducers/siteSettingsReducer';
import portalDomainsReducer from 'ContentEditorUI/redux/reducers/portalDomainsReducer';
import templateInfoReducer from 'ContentEditorUI/redux/reducers/templateInfoReducer';
import themeReducer from 'ContentEditorUI/redux/reducers/themeReducer';
import uploadedFeaturedImagesReducer from 'ContentEditorUI/redux/reducers/uploadedFeaturedImagesReducer';
import contentDataReducer from 'ContentEditorUI/redux/reducers/contentReadOnlyDataReducer';
import layoutDataReducer from 'ContentEditorUI/redux/reducers/layoutDataReducer';
import languageReducer from 'ContentEditorUI/redux/reducers/languageReducer';
import hardSavedContentModelReducer from 'ContentEditorUI/redux/reducers/contentModel/hardSavedContentModelReducer';
import styleBreakpointsReducer from 'ContentEditorUI/redux/reducers/styleBreakpointsReducer';
import contentAssetsReducer from 'ContentEditorUI/redux/reducers/contentAssetsReducer';
import accountVerificationStatusReducer from 'ContentEditorUI/redux/reducers/accountVerificationStatusReducer';
import optimizationsReducer from 'ContentEditorUI/redux/reducers/optimizationsReducer';
import inpageOptimizationsReducer from 'ContentEditorUI/redux/reducers/inpageOptimizationReducer';
import headerReducer from 'ContentEditorUI/redux/reducers/headerReducer';
import { categorizationReducer } from 'ContentEditorUI/redux/reducers/categorizationReducer';

// Inpage Editor
import inpageReducer from 'ContentEditorUI/redux/reducers/inpageReducer';
import inpageModals from 'tinymce-plugins/reducers/InpageModals';
import testFlagsReducer from 'ContentEditorUI/redux/reducers/testFlagsReducer';

// Content assistant
import contentAssistantReducer from 'ContentEditorUI/redux/reducers/contentAssistantReducer';
import audioNarration from './audioNarrationReducer';

// Reducers from blog-editor-ui ---------------------------------------------------------------------
import postContentModelReducer, { postContentModelReducerAsJSObject } from './postContentModelReducer';
import postSettingsReducer from './postSettingsReducer';
import ampReducer from './ampReducer';
import contentGroupReducer from './contentGroupReducer';
import postRequestStatesReducer from './postRequestStatesReducer';
import blogLimitReducer from './blogLimitReducer';
import { sidebarStateReducer } from 'ContentEditorUI/redux/reducers/sidebarStateReducer';
import socialComposerReducer from './socialComposerReducer';
import fakeUndoableReducer, { fakeUndoableReducerAsJSObject } from 'ContentEditorUI/redux/reducers/fakeUndoableReducer';
import reduceReducers from 'ContentEditorUI/utils/reduceReducers';
import sectionsReducer from 'ContentEditorUI/redux/reducers/sectionsReducer';
import immutableToImmerReducer from 'ContentEditorUI/redux/reduxReducerDiffer';
import { runDiffsOnFiftyPercentOfAppLoads, runDiffsOnTwentyFivePercentOfAppLoads } from 'ContentEditorUI/redux/reducers/reduxReducerDifferUtils';
import { gate } from 'hub-http/gates';
import produce from 'immer';
enableModuleReducerDiffing();
const updateSliceImmer = (key, reducer) => (state, action) => {
  return produce(state, draft => {
    draft[key] = reducer(draft[key], action);
  });
};
const updateStateSlice = (key, reducer) => (state, action) => {
  return state.update(key, sliceState => reducer(sliceState, action));
};
const immutableBasedUndoableModuleReducer = createUndoableModuleReducer(reduceReducers(moduleReducer, updateStateSlice('fakeUndoable', fakeUndoableReducer)));
const immerBasedUndoableModuleReducer = createUndoableModuleReducerAsPOJO(reduceReducers(moduleReducerAsJSObject, updateSliceImmer('fakeUndoable', fakeUndoableReducerAsJSObject)));
const diffedUndoableModuleReducer = immutableToImmerReducer(immutableBasedUndoableModuleReducer, immerBasedUndoableModuleReducer, {
  reducerName: 'modules',
  reducerToReturn: {
    type: 'gated',
    gateName: gate('ContentEditorUI:ImmerModuleSelectors')
  },
  prodConfig: {
    computeDiffOnProd: 'conditionally',
    condition: runDiffsOnTwentyFivePercentOfAppLoads
  },
  serializeImmutableState: immutableState => immutableState.get('present').toJS(),
  computeStateDiff: (immutablePresentState, immerUndoableState) => {
    return compareModuleReducerSlices(immutablePresentState, immerUndoableState.present);
  }
});
export default combineReducers({
  // ReduxMessenger reducer
  notifications: Notifications,
  // Reducers from CEUI
  accountVerificationStatus: accountVerificationStatusReducer,
  appStatus: appStatusReducer,
  audioNarration,
  auth: authReducer,
  baseSettings: baseSettingsReducer,
  brandSettings: brandSettingsReducer,
  categorizations: categorizationReducer,
  collaborationSidebar: createCollaborationSidebarReducer(),
  contentReadOnlyData: contentDataReducer,
  contentAssets: contentAssetsReducer,
  contentAssistant: contentAssistantReducer,
  contentModel: immutableToImmerReducer(postContentModelReducer, postContentModelReducerAsJSObject, {
    reducerName: 'contentModel',
    reducerToReturn: {
      type: 'gated',
      gateName: 'ContentEditorUI:ImmerContentModel'
    },
    prodConfig: {
      computeDiffOnProd: 'conditionally',
      condition: runDiffsOnFiftyPercentOfAppLoads
    }
  }),
  contentSchema: contentSchemaReducer,
  panesVisitStatus: panesVisitStatusReducer,
  portalDomains: portalDomainsReducer,
  portal: portalReducer,
  editingInfo: editingInfoReducer,
  editSidebar: editSidebarReducer,
  extraInitialState: extraInitialStateReducer,
  saveData: saveDataReducer,
  formStatus: formStatusReducer,
  iframes: iframesReducer,
  inpageData: inpageReducer,
  inpageModals,
  justSavedModule: justSavedModuleReducer,
  locallyCachedSettings: locallyCachedSettingsReducer,
  moduleErrors: moduleErrorReducer,
  deleteRequest: deleteRequestReducer,
  moduleSchemas: moduleSchemaReducer,
  modules: diffedUndoableModuleReducer,
  siteSettings: siteSettingsReducer,
  resources,
  styleBreakpoints: styleBreakpointsReducer,
  requestStates: postRequestStatesReducer,
  revisions: revisionsReducer,
  selection: selectionReducer,
  sidebarState: sidebarStateReducer,
  smartView: smartViewReducer,
  templateInfo: templateInfoReducer,
  testFlags: testFlagsReducer,
  theme: themeReducer,
  uploadedFeaturedImages: uploadedFeaturedImagesReducer,
  users: usersReducer,
  layoutData: layoutDataReducer,
  languages: languageReducer,
  userAttributes: userAttributesReducer,
  hardSavedContentModel: hardSavedContentModelReducer,
  modals: modalReducer,
  optimizationState: optimizationsReducer,
  inpageOptimizationState: inpageOptimizationsReducer,
  header: headerReducer,
  __location: locationReducer,
  cmsSettings: cmsSettingsReducer,
  moduleVersions: moduleVersionsReducer,
  // Reducers from blog-editor-ui
  amp: ampReducer,
  postSettings: postSettingsReducer,
  contentGroup: contentGroupReducer,
  socialComposer: socialComposerReducer,
  contentTypeLimit: blogLimitReducer,
  sections: sectionsReducer
});